import isNil from 'lodash-es/isNil';
import fabric from './fabric';
import utils from './utils';

// Geometry

const RAD_90 = Math.PI / 2;
const RAD_180 = Math.PI;
const RAD_270 = Math.PI * (3 / 2);
const RAD_360 = Math.PI * 2;

const extrapolatePoints = (p1, p2, ratio) =>
  new fabric.Point(p1.x + (p2.x - p1.x) * ratio, p1.y + (p2.y - p1.y) * ratio);

export const getGradientAngleFromFabricGradientCoords = (
  coords,
  offset = -RAD_90,
) => {
  if (!coords) {
    return offset;
  }
  const dx = coords.x2 - coords.x1;
  const dy = coords.y2 - coords.y1;
  const radians = ((Math.atan2(dy, dx) + RAD_360) % RAD_360) + offset; // output 0 - 2PI
  return fabric.util.radiansToDegrees(radians);
};

export const getCSSGradientStopsFromFabricGradientColorStops = (
  colorStops,
  opacityFactor = 1,
) =>
  colorStops
    .map(colorStop => {
      const rgb = utils.parseRGBA(colorStop.color);
      const c = utils.getRGBAStringFromRGBAObject({
        ...rgb,
        a: (!isNil(colorStop.opacity) ? colorStop.opacity : 1) * opacityFactor,
      });
      const offset = `${colorStop.offset * 100}%`;
      return `${c} ${offset}`;
    })
    .join(',');

export const getCSSGradientFromFabricGradient = (gradient, opacityFactor) => {
  const { angle, coords, type, colorStops } = gradient;
  const colorsStr = getCSSGradientStopsFromFabricGradientColorStops(
    colorStops,
    opacityFactor,
  );
  if (type === 'linear') {
    const degrees =
      180 +
      (!isNil(angle)
        ? angle
        : getGradientAngleFromFabricGradientCoords(coords));
    return `linear-gradient(${degrees}deg, ${colorsStr})`;
  }
  return `radial-gradient(closest-side at center, ${colorsStr})`;
};

export const getLinearCSSGradientFromFabricGradient = (
  gradient,
  angle = 90,
) => {
  const { colorStops } = gradient;
  const colorsStr = getCSSGradientStopsFromFabricGradientColorStops(colorStops);
  return `linear-gradient(${angle}deg, ${colorsStr})`;
};

export const getRGBAStringFromFabricGradientColorStop = colorStop => {
  if (!colorStop) {
    return utils.getRGBAStringFromRGBAObject({
      r: 0,
      g: 0,
      b: 0,
      a: 0,
    });
  }
  const rgb = utils.parseRGBA(colorStop.color);
  return utils.getRGBAStringFromRGBAObject({
    ...rgb,
    a: !isNil(colorStop.opacity) ? colorStop.opacity : 1,
  });
};
export const getFabricGradient = (
  gradientData,
  width,
  height,
  offsetX = 0,
  offsetY = 0,
  centered = true,
) => {
  const { type, angle, colorStops } = gradientData;

  const w = width;
  const h = height;
  const tl = centered
    ? new fabric.Point(-w / 2, -h / 2)
    : new fabric.Point(0, 0);
  const tr = centered
    ? new fabric.Point(w / 2, -h / 2)
    : new fabric.Point(w, 0);
  const bl = centered
    ? new fabric.Point(-w / 2, h / 2)
    : new fabric.Point(0, h);
  const br = centered ? new fabric.Point(w / 2, h / 2) : new fabric.Point(w, h);

  const realCenter = new fabric.Point(0, 0);
  const center = tl.midPointFrom(br);

  let coords;
  let gradientTransform;

  if (type === 'radial') {
    coords = {
      x1: center.x,
      y1: center.y,
      x2: center.x,
      y2: center.y,
      r1: 0,
      r2: w / 2,
    };

    const tx = centered ? w / 2 : 0;
    const ty = centered ? h / 2 : 0;
    gradientTransform = [1, 0, 0, 1, tx, ty];
  } else {
    const rad = fabric.util.degreesToRadians(angle) % RAD_360;
    let cornerPoint;
    let side;
    let offset;

    if (rad >= 0 && rad < RAD_90) {
      cornerPoint = tr;
      side = Math.abs(cornerPoint.y - center.y);
      offset = 0;
    } else if (rad >= RAD_90 && rad < RAD_180) {
      cornerPoint = br;
      side = Math.abs(cornerPoint.x - center.x);
      offset = RAD_90;
    } else if (rad >= RAD_180 && rad < RAD_270) {
      cornerPoint = bl;
      side = Math.abs(cornerPoint.y - center.y);
      offset = RAD_180;
    } else {
      cornerPoint = tl;
      side = Math.abs(cornerPoint.x - center.x);
      offset = RAD_270;
    }

    const hyp = center.distanceFrom(cornerPoint);
    const cornerAngle = (Math.acos(side / hyp) + RAD_360) % RAD_360; // output 0 - 2PI
    const angleDiff = Math.abs(cornerAngle - (rad - offset));
    const dist = hyp * Math.cos(angleDiff);

    const x1 = center.x + dist * Math.sin(rad);
    const y1 = center.y - dist * Math.cos(rad);
    const p1 = new fabric.Point(x1, y1);
    const p2 = extrapolatePoints(p1, center, 2);
    const x2 = p2.x;
    const y2 = p2.y;

    coords = {
      x1: realCenter.x + offsetX + x1,
      y1: realCenter.y + offsetY + y1,
      x2: realCenter.x + offsetX + x2,
      y2: realCenter.y + offsetY + y2,
    };
  }

  return new fabric.Gradient({
    type,
    coords,
    colorStops,
    gradientTransform,
  });
};
